import { call, put, select, takeEvery } from 'redux-saga/effects';

import logger from '@elab/core/utils/logger';
import Breakpoints from '@elab/core/utils/Breakpoints';
import { selectors as globalSelectors } from '@elab/core/store/global';
import { actions as uiActions, selectors as uiSelectors } from '@elab/core/store/ui';
import { getCartId, getUserId } from '@elab/core/utils/user/sagaHelpers';
import * as configSelectors from '@elab/core/store/config/selectors';
import { selectors as deliverySelectors } from '@elab/core/store/cart';

import constants from '../../utils/constants';

import loadGoogleMapsApiSaga from './loadGoogleMapsApiSaga';
import applyDeliveryPostSaga from './applyDeliveryPostSaga';
import requestPostPointDeliverySaga from './requestPostPointDeliverySaga';
import {
    PAGINATION_DEFAULT_PAGE,
    PAGINATION_DEFAULT_PAGE_SIZE,
    SEARCH_TYPE_POST_OFFICE,
    TAB_NAME_MAP_VIEW,
} from './config';
import api from './api';

import { actions, selectors, types } from '.';

export const getPagination = (
    storeList = [],
    currentPage = PAGINATION_DEFAULT_PAGE,
    pageSize = PAGINATION_DEFAULT_PAGE_SIZE,
) => ({
    totalPages: Math.ceil(storeList.length / pageSize),
    totalResults: storeList.length,
    stores: storeList.slice(currentPage * pageSize, (currentPage + 1) * pageSize),
    currentPage,
    pageSize,
});

export function* setSelectedStore({ payload }) {
    const windowSize = yield select(uiSelectors.getWindowSize);

    if (Breakpoints.isMobile(windowSize.width) && payload.triggerMap) {
        yield put(uiActions.openTab(TAB_NAME_MAP_VIEW));
    }
}

export function* setCurrentPage({ payload }) {
    const stores = yield select(selectors.getStores);
    const pagination = yield call(getPagination, stores, payload.page, payload.size);
    yield put(actions.setCurrentPage(pagination));
}

export function* setStores(response = {}, payload) {
    const stores = response.stores || response.results || [];
    yield put(actions.setErrorState(false));
    yield put(actions.setStoresData(response));
    yield put(actions.saveAPIParameters(payload));

    // Frontend pagination
    if (payload.paginationQuantity) {
        const pagination = yield call(
            getPagination,
            stores,
            PAGINATION_DEFAULT_PAGE,
            payload.paginationQuantity,
        );
        yield put(actions.setCurrentPage(pagination));
    }
}

export function* findStores({ payload }) {
    try {
        const lang = yield select(globalSelectors.getLanguage);
        const requestParams = { lang, ...payload };
        const doSendAdditionalParameter = yield select(configSelectors.getStoreAdditionalParameter);
        const deliveryModeToCheck = yield select(
            configSelectors.getAdditionalParameterDeliveryMode,
        );
        const selectedDeliveryMode = yield select(deliverySelectors.getDeliveryMode);
        if (
            doSendAdditionalParameter &&
            deliveryModeToCheck.includes(selectedDeliveryMode.code) &&
            payload.addAdditionalParameter
        ) {
            requestParams.pickupAvailable = true;
        }
        delete requestParams.addAdditionalParameter; // removing flag value from payload
        const response = yield call(api.getStores, requestParams);
        yield call(setStores, response, payload);
    } catch (e) {
        yield put(actions.setErrorState(true));
        yield call(logger.error, e);
    }
}

export function* findPostalOffices({ payload }) {
    try {
        const lang = yield select(globalSelectors.getLanguage);
        const usePostPointAdditionalParams = yield select(
            configSelectors.usePostPointAdditionalParams,
        );
        const deliveryMethodCode = payload.deliveryMethodCode || '';
        const { city, postalCode, currentPage, pageSize, searchType } = payload;
        let requestParams = { currentPage, pageSize, lang };
        if (
            deliveryMethodCode &&
            deliveryMethodCode.includes(constants.ppDeliveryType) &&
            usePostPointAdditionalParams
        ) {
            requestParams = {
                ...requestParams,
                postalCode,
                city,
            };
        } else {
            const { query: postalCode } = payload;
            requestParams = {
                ...requestParams,
                searchType,
                postalCode,
            };
        }

        // TODO: parameters for this call could be changed
        const response = yield call(api.getPostOfficesByPostalCode, requestParams);
        yield call(setStores, response, payload);
    } catch (e) {
        yield put(actions.setErrorState(true));
        yield call(logger.error, e);
    }

    if ((payload.modalName && !Breakpoints.mobileDevice) || payload.showPopupMobile) {
        yield put(uiActions.openDialog({ name: payload.modalName }));
    }
}

export function* getStores({ payload }) {
    if (payload.searchType === SEARCH_TYPE_POST_OFFICE) {
        yield call(findPostalOffices, { payload });
    } else {
        yield call(findStores, { payload });
    }
}

export function* getStoresByTown({ payload }) {
    try {
        const { pointOfServices } = yield call(api.getStoresByTown, payload);
        yield call(setStores, { stores: pointOfServices }, payload);
    } catch (e) {
        yield call(setStores, {}, payload);
    }
}

export function* getSuggestedStores({ payload }) {
    try {
        const userId = yield call(getUserId);
        const cartId = yield call(getCartId, userId);
        const requestParams = { userId, cartId, radius: payload.radius };
        const response = yield call(api.getSuggestedStores, requestParams);
        yield call(setStores, response, payload);
    } catch (e) {
        yield put(actions.setErrorState(true));
        yield call(logger.error, e);
    }
}

export default [
    takeEvery(types.GET_STORES, getStores),
    takeEvery(types.GET_STORES_BY_TOWN, getStoresByTown),
    takeEvery(types.GET_SUGGESTED_STORES, getSuggestedStores),
    takeEvery(types.SELECT_STORE, setSelectedStore),
    takeEvery(types.GET_PAGE, setCurrentPage),
    // Sagas related to stores
    takeEvery(types.LOAD_GOOGLE_MAPS_API, loadGoogleMapsApiSaga),
    takeEvery(types.APPLY_DELIVERY_POST_POINT, applyDeliveryPostSaga),
    takeEvery(types.REQUEST_POST_POINT_DELIVERY, requestPostPointDeliverySaga),
];
